import React, { useCallback, useMemo, useState } from 'react'
import { Alert, Box, CircularProgress, IconButton, Slide, Snackbar, TextField, Typography, ButtonBase } from '@mui/material'
import Select from '@mui/material/Select'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AttendanceStepper from '../AttendanceStepper'

// services
import { useMutation, useQueryClient } from 'react-query'
import { postClockIn, postPauseAttendance, postResumeAttendance } from 'services/api/time-attendance'
import { format } from 'date-fns'
import { formatTimer } from 'services/helper/DateTime'

// icons
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CachedIcon from '@mui/icons-material/Cached'
import ErrorIcon from '@mui/icons-material/Error'
import SquareIcon from '@mui/icons-material/Square'
import CancelIcon from '@mui/icons-material/Cancel'
import PauseIcon from '@mui/icons-material/Pause'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AdxButton from 'components/custom/AdxButton'
import MapImage from '../MapImage'

// Helper functions for location accuracy display
const getGPSAccuracyColor = (accuracy) => {
  if (!accuracy) return '#9E9E9E'
  if (accuracy <= 10) return '#00E4AA'
  if (accuracy <= 20) return '#7ED321'
  if (accuracy <= 50) return '#F3BF3A'
  if (accuracy <= 100) return '#FF9500'
  return '#FF6B6B'
}

const normalizeOutOfRadiusAction = (value) => {
  if (value === 'warn' || value === 2 || value === '2') return 'warn'
  return 'block'
}

const getClockInLocationError = (payload) => {
  return (
    payload?.data?.location_error ||
    payload?.response?.data?.data?.location_error ||
    payload?.data?.data?.location_error ||
    null
  )
}

const getClockInLocationWarning = (payload) => {
  return payload?.data?.location_warning || null
}

const getClockInErrorMessage = (error) => {
  const locationError = getClockInLocationError(error)
  if (locationError?.message) return locationError.message
  if (error?.response?.data?.message) return error.response.data.message
  if (error?.data?.message) return error.data.message
  if (error?.message) return error.message
  return 'Failed to clock in'
}

const formatOutsideZoneDistance = (distanceMeters) => {
  const distance = Number(distanceMeters)
  if (!Number.isFinite(distance) || distance <= 0) return 'outside the allowed zone'
  if (distance >= 1000) {
    const km = distance / 1000
    return `${km.toFixed(km >= 10 ? 0 : 1)} km`
  }

  return `${Math.round(distance)} m`
}

const WarningSlideTransition = (props) => <Slide {...props} direction="down" />

const OutsideZonePanel = ({ distanceLabel, zoneName, onRetry }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: '24px',
      paddingTop: '28px',
      paddingBottom: '8px',
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 160 130"
      sx={{
        width: 160,
        height: 130,
      }}
      aria-hidden="true"
    >
      <g fill="none" stroke="#E9EDF3" strokeWidth="1.5" strokeLinecap="round">
        <path d="M26 22 64 10" />
        <path d="M20 42 78 24" />
        <path d="M24 67 95 44" />
        <path d="M33 92 109 67" />
        <path d="M48 112 120 88" />
        <path d="M52 12 36 105" />
        <path d="M78 18 62 112" />
        <path d="M103 26 87 119" />
        <path d="M127 36 111 124" />
      </g>
      <circle cx="95" cy="61" r="23" fill="#F45B5B" opacity="0.12" />
      <path
        d="M77 66c0-10.493 8.507-19 19-19s19 8.507 19 19c0 14.16-19 30-19 30S77 80.16 77 66Z"
        fill="#F45B5B"
      />
      <circle cx="96" cy="66" r="7.5" fill="#FFF" />
      <circle cx="117" cy="48" r="7.5" fill="#F45B5B" />
      <path d="m114 45 6 6M120 45l-6 6" stroke="#FFF" strokeWidth="2" strokeLinecap="round" />
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <Typography
        sx={{
          fontSize: '28px',
          lineHeight: '42px',
          color: '#333333',
          fontWeight: 400,
        }}
      >
        Outside the zone
      </Typography>

      <Typography sx={{ color: 'light-gray-text.main' }}>
        You are currently {distanceLabel} away from {zoneName || 'the office'}. Move
        closer to the office to clock in
      </Typography>
    </Box>

    <Box
      component="ul"
      sx={{
        margin: 0,
        paddingLeft: '22px',
        alignSelf: 'stretch',
        color: 'light-gray-text.main',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        '& li': {
          textAlign: 'left',
          lineHeight: 1.6,
        },
      }}
    >
      <li>You are currently {distanceLabel} away from the allowed zone.</li>
      <li>Contact your manager if you believe this is an error.</li>
    </Box>

    <AdxButton
      variant="contained"
      onClick={onRetry}
      disableElevation
      sx={{
        width: '100%',
        borderRadius: '100px',
        textTransform: 'none',
        backgroundColor: '#F3BF3A',
        color: '#000',
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#C99700', boxShadow: 'none' },
      }}
    >
      Retry location
    </AdxButton>
  </Box>
)

/**
 * Small internal UI component:
 * - Makes ONLY clock-in state a solid yellow circle (like your screenshot)
 * - Keeps pause/resume styles unchanged
 */
const ActionCircleButton = ({
  active,
  isPaused,
  isBusy,
  isDisabled,
  onClockIn,
  onTogglePauseResume,
}) => {
  const isClockInState = !active

  const handleClick = () => {
    if (isDisabled || isBusy) return
    if (isClockInState) return onClockIn?.()
    return onTogglePauseResume?.()
  }

  return (
    <ButtonBase
      disabled={isDisabled || isBusy}
      onClick={handleClick}
      sx={{
        width: 88,
        height: 88,
        borderRadius: '999px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',

        backgroundColor: isDisabled || isBusy
          ? 'default-background.main'
          : isClockInState
            ? '#F3BF3A'
            : '#FBF5E2',

        border: isClockInState ? 'none' : '1px solid',
        borderColor: isDisabled || isBusy ? 'default-background.main' : '#F3BF3A',

        boxShadow: isClockInState && !isDisabled && !isBusy ? '0 6px 14px rgba(0,0,0,0.08)' : 'none',

        cursor: isDisabled || isBusy ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        transition: 'transform 120ms ease, opacity 120ms ease',
        '&:active': { transform: isDisabled || isBusy ? 'none' : 'scale(0.98)' },
        '&:disabled': { opacity: 0.6 },
      }}
      aria-label={!active ? 'Clock in' : isPaused ? 'Resume attendance' : 'Pause attendance'}
    >
      {!isBusy && (
        <>
          {!active && (
            <PlayArrowIcon
              sx={{
                color: isDisabled ? 'gray-inactive-button.main' : '#000',
                fontSize: 44,
                ml: 0.6,
              }}
            />
          )}

          {active && !isPaused && <PauseIcon sx={{ color: '#000', fontSize: 36 }} />}
          {active && isPaused && <PlayArrowIcon sx={{ color: '#000', fontSize: 44 }} />}
        </>
      )}

      {isBusy && (
        <CircularProgress
          size={88}
          thickness={1}
          sx={{ color: '#F3BF3A', position: 'absolute', zIndex: 1 }}
        />
      )}
    </ButtonBase>
  )
}

const ClockIn = ({ props }) => {
  const {
    permission,
    active,
    time,
    isPaused,
    setIsPaused,
    coordinates,
    locationLoading,
    getUserLocation,
    locationData,
    setPage,
    steps,
    showMap,
    toggleMap,
    setStampedClockOutTime,
    data,
    setErrorSnackbarOpen,
    setSuccessSnackbarOpen,
    setWarningSnackbarOpen,
    shiftZone,
    setDrawerHeaderVariant,
  } = props

  const [notes, setNotes] = useState(null)
  const [workingMode, setWorkingMode] = useState(1)
  const activeStep = 0
  const [outsideZoneState, setOutsideZoneState] = useState(null)
  const [localWarningMessage, setLocalWarningMessage] = useState('')

  const queryClient = useQueryClient()

  const hasCoordinates = useMemo(() => {
    return !!coordinates && coordinates.latitude != null && coordinates.longitude != null
  }, [coordinates])

  const validation = coordinates?.validation
  const isPoorAccuracy = validation?.level === 'poor'
  const normalizedOutOfRadiusAction = normalizeOutOfRadiusAction(shiftZone?.out_of_radius_action)

  const isClockInDisabled = useMemo(() => {
    return locationLoading && !hasCoordinates
  }, [locationLoading, hasCoordinates])

  const invalidateAttendance = useCallback(() => {
    queryClient.invalidateQueries(['getAttendanceDashboard'], { exact: false })
  }, [queryClient])

  const handleChange = (event) => setWorkingMode(event.target.value)
  const handleNoteChange = (e) => setNotes(e.target.value)

  const { mutate: mutateClockIn, isLoading: clockInLoading } = useMutation(postClockIn, {
    onSuccess: async (response) => {
      const locationError = getClockInLocationError(response)
      if (
        response?.success === false &&
        locationError?.code === 'OUT_OF_RADIUS' &&
        normalizedOutOfRadiusAction === 'block'
      ) {
        setErrorSnackbarOpen?.(locationError.message || response?.message || 'Clock-in blocked')
        setOutsideZoneState({
          distanceLabel: formatOutsideZoneDistance(locationError?.distance_meters),
        })
        return
      }

      const warningMessage = getClockInLocationWarning(response)?.message

      if (warningMessage) {
        const nextWarningMessage = `${response?.message || 'Clock-in successfully recorded'}. ${warningMessage}`
        setLocalWarningMessage(nextWarningMessage)
        setWarningSnackbarOpen?.(nextWarningMessage)
        setTimeout(() => {
          invalidateAttendance()
        }, 250)
        return
      }

      setOutsideZoneState(null)
      invalidateAttendance()
      setSuccessSnackbarOpen?.(response?.message || 'Clock-in successful')
    },
    onError: (error) => {
      const locationError = getClockInLocationError(error)
      if (
        locationError?.code === 'OUT_OF_RADIUS' &&
        normalizedOutOfRadiusAction === 'block'
      ) {
        setErrorSnackbarOpen?.(locationError.message || 'Clock-in blocked')
        setOutsideZoneState({
          distanceLabel: formatOutsideZoneDistance(locationError?.distance_meters),
        })
        return
      }

      setErrorSnackbarOpen?.(getClockInErrorMessage(error))
    },
  })

  const { mutate: mutatePause, isLoading: pauseLoading } = useMutation(postPauseAttendance, {
    onSuccess: async () => {
      invalidateAttendance()
      if (!props?.suppressPauseResumeToast) {
        setSuccessSnackbarOpen?.('Paused successfully')
      }
    },
    onError: (error) => {
      setErrorSnackbarOpen?.(error?.response?.data?.message || 'Failed to pause')
    },
  })

  const { mutate: mutateResume, isLoading: resumeLoading } = useMutation(postResumeAttendance, {
    onSuccess: async () => {
      invalidateAttendance()
      if (!props?.suppressPauseResumeToast) {
        setSuccessSnackbarOpen?.('Resumed successfully')
      }
    },
    onError: (error) => {
      setErrorSnackbarOpen?.(error?.response?.data?.message || 'Failed to resume')
    },
  })

  const isBusy = clockInLoading || pauseLoading || resumeLoading

  const buildPauseResumePayload = useCallback(() => {
    const formattedTime = format(new Date(), 'HH:mm:ss')

    const modeId = data?.clockin?.mode_id ?? data?.mode_id ?? workingMode

    const payload = {
      attendance_id: data?.id,
      mode_id: modeId,
      timestamp: formattedTime,
    }
    if (coordinates?.latitude != null) payload.latitude = String(coordinates.latitude)
    if (coordinates?.longitude != null) payload.longitude = String(coordinates.longitude)

    if (coordinates?.accuracy != null) payload.accuracy = coordinates.accuracy
    if (coordinates?.source) payload.location_source = coordinates.source

    return payload
  }, [data, workingMode, coordinates])

  const handleClockIn = useCallback(() => {
    if (!hasCoordinates) {
      getUserLocation?.()
      setErrorSnackbarOpen?.('Please enable location to clock in.')
      return
    }

    setOutsideZoneState(null)

    if (isPoorAccuracy) {
      setErrorSnackbarOpen?.(
        (validation?.message || 'Location accuracy is low.') +
        ' We will still record your clock-in and flag it as approximate.'
      )
    }

    const formattedTime = format(new Date(), 'HH:mm:ss')

    mutateClockIn({
      attendance_id: data?.id,
      mode_id: workingMode,
      clockin: formattedTime,
      latitude: String(coordinates?.latitude),
      longitude: String(coordinates?.longitude),
      remarks: notes,
      street: locationData?.formatted_address || locationData?.display_name,
      accuracy: coordinates?.accuracy,
      location_source: coordinates?.source,
      location_readings: coordinates?.readings,
    })
  }, [
    hasCoordinates,
    isPoorAccuracy,
    validation,
    mutateClockIn,
    data,
    workingMode,
    coordinates,
    notes,
    locationData,
    setErrorSnackbarOpen,
    getUserLocation,
  ])

  const handlePauseResume = useCallback(() => {
    if (!active) return
    if (pauseLoading || resumeLoading) return

    if (!data?.id) {
      setErrorSnackbarOpen?.('No attendance ID found.')
      return
    }

    const alreadyClockedOut = !!data?.clockout?.clockout
    if (alreadyClockedOut) return

    const modeId = data?.clockin?.mode_id ?? data?.mode_id ?? workingMode
    if (!modeId) {
      setErrorSnackbarOpen?.('Missing mode_id. Please clock-in again.')
      return
    }

    const payload = buildPauseResumePayload()
    const wasPaused = !!isPaused

    if (!wasPaused) {
      setIsPaused?.(true)

      mutatePause(payload, {
        onError: () => setIsPaused?.(false),
      })
      return
    }

    mutateResume(payload, {
      onSuccess: () => {
        setIsPaused?.(false)
      },
      onError: () => {
        setIsPaused?.(true)
      },
    })
  }, [
    active,
    pauseLoading,
    resumeLoading,
    data,
    workingMode,
    isPaused,
    setIsPaused,
    setErrorSnackbarOpen,
    buildPauseResumePayload,
    mutateResume,
    mutatePause,
  ])

  const headerText = useMemo(() => {
    if (clockInLoading) return 'Starting...'
    if (pauseLoading) return 'Pausing...'
    if (resumeLoading) return 'Resuming...'

    if (locationLoading && !hasCoordinates) return 'Detecting your location, please wait...'
    if (permission === 'denied') return 'Location access blocked'
    if (locationLoading && !coordinates?.accuracy) return 'Detecting your location, please wait...'

    if (!active) return 'Start now'
    return isPaused ? 'Paused' : "You're clocked in"
  }, [clockInLoading, pauseLoading, resumeLoading, permission, locationLoading, coordinates, hasCoordinates, active, isPaused])

  const goClockOut = useCallback(() => {
    const currentTime = new Date()
    setStampedClockOutTime(currentTime)
    setPage(2)
  }, [setStampedClockOutTime, setPage])

  const renderSection = () => {
    switch (active) {
      case false:
        return (
          <LocationSection
            getUserLocation={getUserLocation}
            locationData={locationData}
            permission={permission}
            handleNoteChange={handleNoteChange}
            setPage={setPage}
            coordinates={coordinates}
            locationLoading={locationLoading}
            shiftZone={shiftZone}
          />
        )
      case true:
        return (
          <AttendanceStepper
            steps={steps}
            activeStep={activeStep}
            loading={clockInLoading}
            showMap={showMap || {}}
            toggleMap={toggleMap || (() => { })}
          />
        )
      default:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {steps && steps.length > 0 && (
              <AttendanceStepper
                steps={steps}
                activeStep={activeStep}
                loading={clockInLoading}
                showMap={showMap || {}}
                toggleMap={toggleMap || (() => { })}
              />
            )}

            <LocationSection
              getUserLocation={getUserLocation}
              locationData={locationData}
              permission={permission}
              handleNoteChange={handleNoteChange}
              setPage={setPage}
              coordinates={coordinates}
              locationLoading={locationLoading}
              shiftZone={shiftZone}
            />
          </Box>
        )
    }
  }

  React.useEffect(() => {
    if (!setDrawerHeaderVariant) return undefined

    if (outsideZoneState && normalizedOutOfRadiusAction === 'block') {
      setDrawerHeaderVariant('outsideZone')
    } else {
      setDrawerHeaderVariant('default')
    }

    return () => {
      setDrawerHeaderVariant('default')
    }
  }, [outsideZoneState, normalizedOutOfRadiusAction, setDrawerHeaderVariant])

  return (
    <>
      {!!localWarningMessage && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={!!localWarningMessage}
          onClose={() => setLocalWarningMessage('')}
          autoHideDuration={4000}
          TransitionComponent={WarningSlideTransition}
        >
          <Alert
            severity="warning"
            variant="filled"
            icon={<WarningAmberIcon sx={{ fontSize: 20, color: '#000' }} />}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setLocalWarningMessage('')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography color="black">{localWarningMessage}</Typography>
          </Alert>
        </Snackbar>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
        {outsideZoneState && normalizedOutOfRadiusAction === 'block' ? (
          <OutsideZonePanel
            distanceLabel={outsideZoneState.distanceLabel}
            zoneName={shiftZone?.zone_name}
            onRetry={() => {
              setOutsideZoneState(null)
              getUserLocation?.()
            }}
          />
        ) : (
          <>
            <Select value={workingMode} onChange={handleChange} disabled={clockInLoading || active}>
              <AdxMenuItem selectedvalue={workingMode} value={1}>
                Work from office
              </AdxMenuItem>
              <AdxMenuItem selectedvalue={workingMode} value={2}>
                Working remotely
              </AdxMenuItem>
            </Select>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: '32px',
                gap: '20px',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'light-gray-text.main' }}>
                  {headerText}
                </Typography>

                <Typography variant="h2">{`${formatTimer(time)} hrs`}</Typography>

                {active && (
                  <Typography variant="body2" sx={{ color: 'light-gray-text.main', mt: 0.5 }}>
                    {isPaused ? 'Break time (timer stopped)' : 'Working (timer running)'}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ActionCircleButton
                  active={active}
                  isPaused={isPaused}
                  isBusy={isBusy}
                  isDisabled={isClockInDisabled}
                  onClockIn={handleClockIn}
                  onTogglePauseResume={handlePauseResume}
                />

                <Box sx={{ height: '32px' }} />

                {active && (
                  <AdxButton
                    variant="contained"
                    onClick={goClockOut}
                    disabled={isBusy}
                    disableElevation
                    disableRipple
                    disableFocusRipple
                    startIcon={<SquareIcon sx={{ width: 21, height: 25, color: '#000' }} />}
                    sx={{
                      height: '45px',
                      px: '16px',
                      py: '8px',
                      borderRadius: '100px',
                      border: '1px solid #E5E5E5',
                      boxShadow: 'none',
                      backgroundColor: '#F3BF3A',
                      color: '#000',
                      textTransform: 'none',
                      '& .MuiButton-startIcon': {
                        marginLeft: 0,
                        marginRight: '10px',
                      },
                      '&:hover': {
                        backgroundColor: '#C99700',
                        boxShadow: 'none',
                      },
                      '&.Mui-focusVisible': {
                        outline: 'none',
                        boxShadow: 'none',
                      },
                      '&:disabled': {
                        backgroundColor: '#F3BF3A',
                        opacity: 0.6,
                        color: '#000',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Clock Out
                  </AdxButton>
                )}
              </Box>
            </Box>

            {renderSection()}
          </>
        )}
      </Box>
    </>
  )
}

export default ClockIn

export const LocationSection = ({
  getUserLocation,
  locationData,
  permission,
  handleNoteChange,
  type = 'clockIn',
  setPage,
  coordinates,
  locationLoading,
  geocodingLoading = false,
  geocodingError = false,
  shiftZone,
}) => {
  const hasZoneDisplay =
    Boolean(shiftZone?.enabled) &&
    shiftZone?.latitude != null &&
    shiftZone?.longitude != null

  const hasAccuracy = !!coordinates?.accuracy
  const isPoorAccuracy = coordinates?.validation?.level === 'poor'

  const hasLatLon =
    coordinates?.latitude != null && coordinates?.longitude != null

  const showFetchingAddress =
    permission === 'granted' &&
    hasLatLon &&
    geocodingLoading &&
    !locationData

  const showGeocodingFailed =
    permission === 'granted' &&
    hasLatLon &&
    geocodingError &&
    !locationData

  const hasResolvedLatLon =
    locationData?.resolved_latitude != null &&
    locationData?.resolved_longitude != null

  const shouldShowMap =
    hasZoneDisplay ||
    (
      permission === 'granted' &&
      !locationLoading &&
      hasResolvedLatLon
    )

  const locationHeading = hasZoneDisplay
    ? 'Your Zone'
    : permission === 'denied'
      ? 'Location'
      : 'Your location'

  const displayTitle = hasZoneDisplay
    ? shiftZone?.zone_name || 'Configured zone'
    : permission === 'granted'
      ? showFetchingAddress
        ? 'Fetching address...'
        : showGeocodingFailed
          ? 'Unable to fetch address'
          : locationData?.building_name
            ? locationData.building_name
            : locationData?.primary_location ||
              locationData?.formatted_address ||
              locationData?.display_name ||
              'Location detected'
      : permission === 'denied'
        ? 'Your location is offline'
        : 'Scanning...'

  const displayAddress = hasZoneDisplay
    ? shiftZone?.zone_address
    : permission === 'granted' && locationData
      ? locationData?.formatted_address ||
        locationData?.display_name ||
        'Location details unavailable'
      : null

  const mapLatitude = hasZoneDisplay
    ? shiftZone?.latitude
    : locationData?.resolved_latitude ?? coordinates?.latitude

  const mapLongitude = hasZoneDisplay
    ? shiftZone?.longitude
    : locationData?.resolved_longitude ?? coordinates?.longitude

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: type === 'clockIn' ? 'auto' : null,
        marginBottom: type === 'clockIn' ? '16px' : null,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
        {permission === 'granted' ? (
          <CheckCircleIcon sx={{ fontSize: '18px', color: '#00E4AA' }} />
        ) : permission === 'denied' ? (
          <CancelIcon sx={{ fontSize: '18px', color: 'error.main' }} />
        ) : (
          <ErrorIcon sx={{ fontSize: '18px', color: 'warning.main' }} />
        )}
        <Typography>{locationHeading}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          padding: '14px',
          backgroundColor: 'default-background.main',
          borderRadius: '4.5px',
          marginBottom: '12px',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: permission === 'denied' ? 'error.main' : 'dark-text.main',
              fontWeight:
                hasZoneDisplay || locationData?.building_name || locationData?.primary_location
                  ? 'bold'
                  : 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.3,
            }}
          >
            {displayTitle}
          </Typography>

          {!hasZoneDisplay && showFetchingAddress && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.75rem',
                marginTop: '4px',
              }}
            >
              Reverse geocoding in progress...
            </Typography>
          )}

          {!hasZoneDisplay && showGeocodingFailed && (
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                display: 'block',
                fontSize: '0.75rem',
                marginTop: '4px',
              }}
            >
              Tap refresh to try again.
            </Typography>
          )}

          {!!displayAddress && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.75rem',
                wordBreak: 'break-word',
                lineHeight: 1.2,
                marginTop: '4px',
              }}
            >
              {displayAddress}
            </Typography>
          )}

          {permission === 'granted' && !hasAccuracy && locationLoading && (
            <Typography
              variant="caption"
              sx={{
                color: '#333333',
                fontSize: '0.8rem',
                fontWeight: 600,
                mt: 0.5,
                display: 'block',
              }}
            >
              System is trying to lock your location. This may take up to 30–60 seconds.
            </Typography>
          )}

          {permission === 'granted' && hasAccuracy && (
            <Typography
              variant="caption"
              sx={{
                color: getGPSAccuracyColor(coordinates.accuracy),
                fontSize: '0.75rem',
                fontWeight: 'bold',
                mt: 0.5,
                display: 'block',
              }}
            >
              GPS: ±{Math.round(coordinates.accuracy)}m
              {coordinates.source && ` (${coordinates.source})`}
            </Typography>
          )}

          {permission === 'granted' && isPoorAccuracy && (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: '#FF6B6B',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  mt: 0.25,
                  display: 'block',
                }}
              >
                Location accuracy is low; clock-in will still be recorded and flagged as approximate.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#555555',
                  fontSize: '0.75rem',
                  mt: 0.25,
                  display: 'block',
                }}
              >
                If indoors / Wi-Fi desktop, GPS may remain inaccurate even after 30–60 seconds.
              </Typography>
            </>
          )}
        </Box>

        {locationLoading ? (
          <CircularProgress
            color="white"
            size={21}
            sx={{ boxShadow: 'inset 0 0 0 2px #F3BF3A', borderRadius: '100%' }}
          />
        ) : permission === 'denied' ? (
          <Typography
            sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setPage(0)}
          >
            Reconnect
          </Typography>
        ) : (
          <CachedIcon
            sx={{ fontSize: '21px', color: 'primary.main', cursor: 'pointer' }}
            onClick={() => getUserLocation()}
          />
        )}
      </Box>

      {shouldShowMap && (
        <MapImage
          latitude={mapLatitude}
          longitude={mapLongitude}
          markerLabel={hasZoneDisplay ? 'Your Zone' : 'Your location'}
          width={400}
          height={200}
          zoom={15}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography>{type === 'clockIn' ? 'Clock in notes (Optional)' : 'Clock out notes (Optional)'}</Typography>
        <TextField
          multiline
          rows={4}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': { borderWidth: 1, borderColor: 'primary.main' },
            },
          }}
          placeholder="Any notes related to your clock-in"
          onChange={handleNoteChange}
        />
      </Box>
    </Box>
  )
}
